import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Inbox, Plus, Pencil, Trash2, Download, PlugZap } from 'lucide-react';
import api from '../../api/client';
import Modal from '../shared/Modal';
import LearningMailboxForm from './LearningMailboxForm';
import { cn } from '../../utils/cn';

/**
 * A mailbox that exists only to import from - the archive the shop handled
 * support in before this platform. Separate credentials, never polled, never
 * sends.
 */
export default function LearningMailboxPanel({ shops, onImported }) {
  const { t } = useTranslation();
  const [mailboxes, setMailboxes] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [result, setResult] = useState(null);

  const fetchMailboxes = useCallback(async () => {
    try {
      const { data } = await api.get('/learning-mailboxes');
      setMailboxes(data);
    } catch (error) {
      console.error('Failed to fetch learning mailboxes:', error);
    }
  }, []);

  useEffect(() => { fetchMailboxes(); }, [fetchMailboxes]);

  const handleTest = async (mailbox) => {
    setBusyId(mailbox.id);
    setResult(null);
    try {
      const { data } = await api.post(`/learning-mailboxes/${mailbox.id}/test`);
      setResult({
        id: mailbox.id,
        kind: 'ok',
        text: t('knowledge.mailbox_test_ok', {
          count: data.data.inbox_count ?? '?',
          sent: data.data.sent_folder || t('knowledge.mailbox_no_sent_folder'),
        }),
      });
    } catch (error) {
      setResult({ id: mailbox.id, kind: 'error', text: error.response?.data?.message || t('knowledge.mailbox_test_failed') });
    } finally {
      setBusyId(null);
    }
  };

  const handleImport = async (mailbox) => {
    if (!window.confirm(t('knowledge.mailbox_import_confirm', { since: mailbox.import_since || '?' }))) return;

    setBusyId(mailbox.id);
    setResult(null);
    try {
      const { data } = await api.post(`/learning-mailboxes/${mailbox.id}/import`);
      setResult({ id: mailbox.id, kind: 'ok', text: t('knowledge.mailbox_import_done', data.data) });
      fetchMailboxes();
      onImported?.();
    } catch (error) {
      setResult({ id: mailbox.id, kind: 'error', text: error.response?.data?.message || t('knowledge.mailbox_import_failed') });
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (mailbox) => {
    if (!window.confirm(t('common.delete_confirm'))) return;
    await api.delete(`/learning-mailboxes/${mailbox.id}`);
    fetchMailboxes();
  };

  const openCreate = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (mailbox) => { setEditing(mailbox); setModalOpen(true); };

  const handleSaved = () => { setModalOpen(false); fetchMailboxes(); };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Inbox className="h-4 w-4 text-primary-600" />
            <h2 className="font-semibold text-gray-900">{t('knowledge.mailbox_title')}</h2>
          </div>
          <p className="text-sm text-gray-500 mt-1">{t('knowledge.mailbox_hint')}</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-3 py-1.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors flex-shrink-0"
        >
          <Plus className="h-4 w-4" /> {t('common.add')}
        </button>
      </div>

      {mailboxes.length === 0 ? (
        <p className="text-sm text-gray-500">{t('knowledge.mailbox_empty')}</p>
      ) : (
        <ul className="space-y-3">
          {mailboxes.map((mailbox) => (
            <li key={mailbox.id} className="border border-gray-200 rounded-lg p-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="font-medium text-gray-900">{mailbox.name}</p>
                  <p className="text-xs text-gray-500">
                    {mailbox.imap_user} · {mailbox.shop?.name}
                    {mailbox.import_since && ` · ${t('knowledge.mailbox_since', { date: mailbox.import_since })}`}
                  </p>
                  {mailbox.last_imported_at && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      {t('knowledge.mailbox_last_import', {
                        date: new Date(mailbox.last_imported_at).toLocaleDateString(),
                        count: mailbox.last_imported_count,
                      })}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => handleTest(mailbox)}
                    disabled={busyId === mailbox.id}
                    title={t('knowledge.mailbox_test')}
                    className="p-1.5 text-gray-400 hover:text-primary-600 disabled:opacity-40 transition-colors"
                  >
                    <PlugZap className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleImport(mailbox)}
                    disabled={busyId === mailbox.id}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors"
                  >
                    <Download className="h-4 w-4" />
                    {busyId === mailbox.id ? t('knowledge.mailbox_importing') : t('knowledge.mailbox_import')}
                  </button>
                  <button onClick={() => openEdit(mailbox)} className="p-1.5 text-gray-400 hover:text-primary-600 transition-colors">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => handleDelete(mailbox)} className="p-1.5 text-gray-400 hover:text-red-600 transition-colors">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {result?.id === mailbox.id && (
                <p className={cn('mt-2 text-sm', result.kind === 'ok' ? 'text-green-700' : 'text-red-600')}>
                  {result.text}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? t('knowledge.mailbox_edit_title') : t('knowledge.mailbox_create_title')}
        size="lg"
      >
        <LearningMailboxForm shops={shops} mailbox={editing} onSaved={handleSaved} onCancel={() => setModalOpen(false)} />
      </Modal>
    </div>
  );
}
